/**
 * Design systems in the catalogue (AIOE 0.22.0; docs/plans/2026-10-design-systems.md, round 1):
 * an organisation's look (tokens, art, fonts, logos, component rules, voice, tones) published once
 * as a `design-system` catalogue entry, governed like the rest of the catalogue, and turned into a
 * skill on every machine by {@link designSystemSkill}, so a deck from one machine and a report from
 * a cloud agent come out the same. Mirrored in the workbench's `src/platform/catalogue.ts` beside
 * the catalogue contract; everything here is additive and served only to a caller whose
 * `?include=` names `design-system`, so `CATALOGUE_VERSION` does not change.
 *
 * **Where the pieces live.** Text in the entry's `files` (at most 256 KiB altogether, as for a
 * skill): the tokens stylesheet (`content.tokens` names its path), the art stylesheet
 * (`content.art`), and any references. Binary files (fonts, logos) and stylesheets too large for
 * `files` are assets: uploaded by an administrator with `POST /admin/v1/catalogue/assets`, held in
 * the platform's object store under the organisation, named by the sha256 of their bytes (the same
 * id on every machine), and read with `GET /catalogue/v1/assets/:id` (a device's or a person's
 * token; ETag is the id, cached for good since an id never changes its bytes).
 *
 *   POST /admin/v1/catalogue/assets                    administrators: { name, contentType, data (base64) }
 *                                                      or the bytes with the asset's Content-Type and ?name=
 *                                                      -> 201 DesignSystemAsset (200 when the organisation has it already)
 *   GET  /catalogue/v1/assets/:id                      device or person: the bytes; ETag "<id>", 304 on If-None-Match
 *   GET  /admin/v1/catalogue/:id/versions              administrators: every published version of a design system, newest first
 *   GET  /admin/v1/catalogue/:id/versions/:version     administrators: that version's entry, as it was published
 *   GET  /admin/v1/catalogue/templates/design-systems  administrators: starting points (plain CSS, Tailwind tokens)
 *   POST /admin/v1/catalogue/templates/design-systems/:id/use   administrators: publishes one into the catalogue -> 201 { id, entry }
 *
 * **Checked on publish and on submission** (in words, 400 `invalid_entry`): the content parses;
 * the entry's `version` is the content's; the tokens and art stylesheets are in `files`; no two
 * pieces share one path; a stylesheet's `url()` names only the system's own files or a
 * `data:` address and nothing is imported from another address ({@link designSystemProblems});
 * every asset is the organisation's, of the right kind, and together at most
 * {@link DESIGN_SYSTEM_TOTAL_MAX_BYTES}; at most one entry is the organisation's default
 * (`useByDefault`) and at most one dresses the console (`consoleUse`).
 *
 * **Delivery** as for hooks and plugins: `installed` on every machine, uneditable there, told once
 * in the inbox; `offered` listed for the person to add. Whether a session is told to load the
 * default system is the workbench's policy clause `designSystem.useByDefault`, declared in its
 * policy manifest and governed from the console's settings with no AIOE change.
 *
 * **Versions.** Publishing a design system records that version (who and when); publishing again
 * under a new version keeps the previous one readable. Publishing again under the same version
 * replaces that version's record (a correction).
 */
import { z } from "zod";
import { dateLong } from "../wording/dateTime.js";
import { DESIGN_SYSTEM_KIND, catalogueFilePathSchema, customisationDeliverySchema } from "./catalogue.js";
/** One asset's bytes, at most. */
export const DESIGN_SYSTEM_ASSET_MAX_BYTES = 5 * 1024 * 1024;
/** Every asset one design system names, altogether. */
export const DESIGN_SYSTEM_TOTAL_MAX_BYTES = 40 * 1024 * 1024;
/** What an asset may be: fonts, pictures, stylesheets. Nothing that runs. */
export const DESIGN_SYSTEM_ASSET_TYPES = ["font/woff2", "font/woff", "font/ttf", "font/otf", "image/svg+xml", "image/png", "image/webp", "image/jpeg", "text/css"];
export const designSystemAssetTypeSchema = z.enum(DESIGN_SYSTEM_ASSET_TYPES);
/** An asset's id: the sha256 of its bytes, lower-case hex. */
export const designSystemAssetIdSchema = z.string().regex(/^[0-9a-f]{64}$/, "an asset id is the sha256 of its bytes");
/** `POST /admin/v1/catalogue/assets` as JSON: the bytes base64-encoded. */
export const designSystemAssetUploadSchema = z.object({
    /** The file's name as uploaded (`Inter-Variable.woff2`); shown in the console. */
    name: z.string().min(1).max(200),
    contentType: designSystemAssetTypeSchema,
    /** The bytes, base64. */
    data: z.string().min(1),
});
/** One file of a font family, by asset. */
export const designSystemFontFileSchema = z.object({
    asset: designSystemAssetIdSchema,
    /** Where it lands in the skill folder (`assets/fonts/Inter-Variable.woff2`). */
    path: catalogueFilePathSchema,
    /** CSS font-weight: `400`, or a range for a variable font, `100 900`. */
    weight: z.string().regex(/^\d{3}( \d{3})?$/).optional(),
    style: z.enum(["normal", "italic"]).optional(),
});
/** A font family the system uses. Its licence is required: a font installed on every machine must be licensed for it. */
export const designSystemFontSchema = z.object({
    /** The family name stylesheets use (`Inter`). */
    name: z.string().min(1).max(100),
    files: z.array(designSystemFontFileSchema).min(1).max(24),
    /** The font's licence, in words or an SPDX id (`OFL-1.1`); required. */
    licence: z.string().trim().min(1, "a font says its licence").max(500),
    /** What it is for, one line the model reads (`Body and interface text`). */
    use: z.string().max(300).optional(),
});
/** A logo or another picture the system offers, by asset. */
export const designSystemLogoSchema = z.object({
    asset: designSystemAssetIdSchema,
    path: catalogueFilePathSchema,
    name: z.string().min(1).max(100),
    /** When to use it (`On dark grounds`), one line the model reads. */
    use: z.string().max(300).optional(),
});
/** A stylesheet held as an asset: one too big for `files`, or a font-face sheet. Loaded after tokens and art, in order. */
export const designSystemStylesheetSchema = z.object({
    asset: designSystemAssetIdSchema,
    path: catalogueFilePathSchema,
    use: z.string().max(300).optional(),
});
/** One named tone (a colour an area of a product owns): Mojo Up has seven. */
export const designSystemToneSchema = z.object({
    id: z.string().regex(/^[a-z0-9-]{1,40}$/),
    name: z.string().min(1).max(60),
    /** A CSS colour (`oklch(0.68 0.12 180)`, `#0f766e`). */
    colour: z.string().min(1).max(100).refine((c) => !/[;{}<>]/.test(c), "a colour, not a rule"),
    /** What it marks (`chat, workspace, pipelines`). */
    use: z.string().max(300).optional(),
});
export const designSystemContentSchema = z.object({
    /** The system's name as people say it (`Mojo Up design language`). */
    name: z.string().min(1).max(200),
    /** Its version; the entry's `version` is the same (filled from here when absent). */
    version: z.string().min(1).max(40),
    /** Markdown the model reads first: the body of SKILL.md. */
    readme: z.string().min(1).max(64 * 1024),
    /** When to use it, one sentence; the skill's description. */
    useWhen: z.string().max(500).optional(),
    /** The path in `files` of the tokens stylesheet (CSS custom properties, light and dark). Loaded first. */
    tokens: catalogueFilePathSchema,
    /** The path in `files` of the art stylesheet (surfaces, cards, tones). Loaded after the tokens. */
    art: catalogueFilePathSchema.optional(),
    /** Further stylesheets held as assets. */
    stylesheets: z.array(designSystemStylesheetSchema).max(8).optional(),
    fonts: z.array(designSystemFontSchema).max(12).optional(),
    logos: z.array(designSystemLogoSchema).max(40).optional(),
    /** How components are built and laid out, Markdown. */
    rules: z.string().max(64 * 1024).optional(),
    /** How the organisation writes, Markdown. */
    voice: z.string().max(32 * 1024).optional(),
    tones: z.array(designSystemToneSchema).max(16).optional(),
    /** The component library it stands on, when it has one (`HeroUI`, `3.0`). */
    library: z.object({ name: z.string().min(1).max(100), version: z.string().max(40).optional() }).optional(),
    delivery: customisationDeliverySchema,
    /**
     * The organisation's default design system: what every session is told to load first (the
     * workbench's clause `designSystem.useByDefault` turns the telling off). At most one entry.
     */
    useByDefault: z.boolean().optional(),
    /**
     * The console wears it: the console loads this system's tokens stylesheet after its own. Opt-in
     * and reversible from the console's Design systems page. At most one entry.
     */
    consoleUse: z.boolean().optional(),
});
/** Every asset a design system names, with the kind of thing it must be. */
export function designSystemAssets(c) {
    return [
        ...(c.stylesheets ?? []).map((s) => ({ asset: s.asset, path: s.path, kind: "stylesheet", ...(s.use ? { use: s.use } : {}) })),
        ...(c.fonts ?? []).flatMap((f) => f.files.map((x) => ({ asset: x.asset, path: x.path, kind: "font", use: `${f.name}${x.weight ? ` ${x.weight}` : ""}${x.style === "italic" ? " italic" : ""}` }))),
        ...(c.logos ?? []).map((l) => ({ asset: l.asset, path: l.path, kind: "logo", use: l.use ? `${l.name}: ${l.use}` : l.name })),
    ];
}
/** Whether an asset's type suits what it is named as. */
export function assetTypeFits(kind, contentType) {
    return kind === "font" ? contentType.startsWith("font/") : kind === "logo" ? contentType.startsWith("image/") : contentType === "text/css";
}
/** The folder a path sits in (`assets/tokens.css` -> `assets`), and a relative reference resolved against it. */
function resolvePath(from, ref) {
    const parts = from.split("/").slice(0, -1);
    for (const seg of ref.split("/")) {
        if (seg === "" || seg === ".")
            continue;
        if (seg === "..") {
            if (!parts.length)
                return undefined;
            parts.pop();
        }
        else
            parts.push(seg);
    }
    return parts.join("/");
}
/**
 * What is wrong with a stylesheet's references, in words: every `url()` names a `data:` address or
 * one of the system's own files (by path, relative to the stylesheet), and `@import` names no other
 * address (a package import such as `@heroui-pro/react/themes/glass` is a build's business and
 * passes; `https:`, `//` and `url()` imports do not). The console and the preview load the system's
 * stylesheets into pages, so nothing in them may reach out.
 */
export function cssReferenceProblems(css, at, paths) {
    const problems = [];
    // Read as a browser reads it: comments out and escapes (`u\72l(`, `@\69mport`) decoded, so neither hides a reference.
    const text = css
        .replace(/\/\*[\s\S]*?\*\//g, "")
        .replace(/\\([0-9a-fA-F]{1,6})[ \t\n\r\f]?/g, (_m, hex) => {
        const code = parseInt(hex, 16);
        return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : "�";
    })
        .replace(/\\([^\n\r\f])/g, "$1");
    for (const m of text.matchAll(/url\(\s*(['"]?)([^'")]*)\1\s*\)/gi)) {
        const ref = m[2].trim();
        if (/^data:/i.test(ref) || ref.startsWith("#"))
            continue;
        if (/^[a-z][a-z0-9+.-]*:/i.test(ref) || ref.startsWith("//") || ref.startsWith("/")) {
            problems.push(`${at} loads ${ref.slice(0, 80)} from outside the design system: keep fonts and pictures as its own assets`);
            continue;
        }
        const target = resolvePath(at, ref.split(/[?#]/)[0]);
        if (!target || !paths.has(target))
            problems.push(`${at} names ${ref.slice(0, 80)}, which is not one of the design system's files or assets`);
    }
    for (const m of text.matchAll(/@import\s*(url\()?\s*(['"]?)([^'");\s]*)\2/gi)) {
        const ref = m[3].trim();
        if (/^[a-z][a-z0-9+.-]*:/i.test(ref) || ref.startsWith("//") || ref.startsWith("/"))
            problems.push(`${at} imports ${ref.slice(0, 80)} from another address`);
    }
    // An address as a bare string: `image-set("https://…" 1x)` loads it without a `url()`.
    for (const m of text.matchAll(/(?<!(?:url\(|@import)\s*)(['"])((?:[a-z][a-z0-9+.-]*:)?\/\/[^'"\s]+)\1/gi)) {
        problems.push(`${at} names the address ${m[2].slice(0, 80)}: keep fonts and pictures as the design system's own assets`);
    }
    if (/expression\s*\(|javascript:|-moz-binding|behavior\s*:/i.test(text))
        problems.push(`${at} holds script`);
    return problems;
}
/**
 * What is wrong with a design-system entry, in words, without the asset store: the content parses,
 * the version agrees, the stylesheets are in `files`, no two pieces share a path, and the
 * stylesheets reach nothing outside the system. The API adds the asset checks; the console shows
 * these before publishing. Answers the content as parsed when there is nothing wrong.
 */
export function designSystemProblems(entry) {
    const parsed = designSystemContentSchema.safeParse(entry.content);
    if (!parsed.success) {
        const i = parsed.error.issues[0];
        return { problems: [`the design system is not complete: ${i?.path.join(".") || "content"} ${i?.message ?? ""}`.trim()] };
    }
    const c = parsed.data;
    const problems = [];
    const files = entry.files ?? {};
    if (entry.version && entry.version !== c.version)
        problems.push(`the entry's version (${entry.version}) is not the design system's (${c.version})`);
    if (files[c.tokens] === undefined)
        problems.push(`the tokens stylesheet, ${c.tokens}, is not among the files`);
    if (c.art && files[c.art] === undefined)
        problems.push(`the art stylesheet, ${c.art}, is not among the files`);
    const assets = designSystemAssets(c);
    const seen = new Set(["SKILL.md"]);
    for (const p of [...Object.keys(files), ...assets.map((a) => a.path)]) {
        if (seen.has(p))
            problems.push(`two pieces share the path ${p}`);
        seen.add(p);
    }
    const ids = new Set(c.tones?.map((t) => t.id));
    if (c.tones && ids.size !== c.tones.length)
        problems.push("two tones share an id");
    // The tokens and art stylesheets are loaded whatever their names end in.
    for (const [path, text] of Object.entries(files))
        if (path.endsWith(".css") || path === c.tokens || path === c.art)
            problems.push(...cssReferenceProblems(text, path, seen));
    return problems.length ? { problems } : { problems, content: c };
}
const yamlString = (s) => JSON.stringify(s.replace(/\s+/g, " ").trim());
/**
 * Turns a design-system entry into the skill a workbench installs, so the workbench writes and the
 * console previews the same thing. SKILL.md is the README first, then the component rules, the
 * voice, how to load the stylesheets and fonts (in order, with an `@font-face` for each font
 * file), the logos and the tones; the files are the entry's `files` as text and every asset by id
 * with `assetUrl(id)` (`GET /catalogue/v1/assets/:id` on the platform). Throws when the entry is
 * not a design system that passes {@link designSystemProblems}.
 */
export function designSystemSkill(entry, assetUrl) {
    const { problems, content: c } = designSystemProblems(entry);
    if (!c)
        throw new Error(problems.join("; "));
    const description = `${c.useWhen?.trim() || `${c.name}: the organisation's design system.`} Read this before making any interface, page, deck, document or picture.`;
    const assets = designSystemAssets(c);
    const css = [c.tokens, ...(c.art ? [c.art] : []), ...(c.stylesheets ?? []).map((s) => s.path)];
    const lines = [
        "---",
        `name: ${entry.id}`,
        `description: ${yamlString(description)}`,
        "metadata:",
        `  version: ${yamlString(c.version)}`,
        `  design-system: ${yamlString(c.name)}`,
        ...(c.useByDefault ? ["  default: true"] : []),
        "---",
        "",
        c.readme.trim(),
        "",
    ];
    if (c.rules?.trim())
        lines.push("## Component rules", "", c.rules.trim(), "");
    if (c.voice?.trim())
        lines.push("## Voice", "", c.voice.trim(), "");
    lines.push("## Loading the stylesheets and fonts", "");
    lines.push(`Load these stylesheets in this order, after the component library's own${c.library ? ` (${c.library.name}${c.library.version ? ` ${c.library.version}` : ""})` : ""}:`, "");
    css.forEach((p, i) => lines.push(`${i + 1}. \`${p}\`${p === c.tokens ? ": the tokens (colours, surfaces, radii), light and dark" : p === c.art ? ": the art (surfaces, cards, tones)" : (c.stylesheets ?? []).find((s) => s.path === p)?.use ? `: ${(c.stylesheets ?? []).find((s) => s.path === p).use}` : ""}`));
    lines.push("");
    if (c.fonts?.length) {
        lines.push("Fonts, each with its licence:", "");
        for (const f of c.fonts)
            lines.push(`- **${f.name}**${f.use ? `: ${f.use}` : ""}. Licence: ${f.licence}. Files: ${f.files.map((x) => `\`${x.path}\``).join(", ")}.`);
        lines.push("", "```css");
        for (const f of c.fonts)
            for (const x of f.files) {
                const format = x.path.endsWith(".woff2") ? "woff2" : x.path.endsWith(".woff") ? "woff" : x.path.endsWith(".otf") ? "opentype" : "truetype";
                lines.push(`@font-face { font-family: ${JSON.stringify(f.name)}; src: url(${JSON.stringify(x.path)}) format("${format}");${x.weight ? ` font-weight: ${x.weight};` : ""}${x.style ? ` font-style: ${x.style};` : ""} font-display: swap; }`);
            }
        lines.push("```", "", "Paths are relative to this skill's folder; copy the files next to the page or deck that uses them.", "");
    }
    else
        lines.push("The design system ships no font files: use the families its stylesheets name, from the machine or the product's own packages.", "");
    if (c.logos?.length) {
        lines.push("## Logos", "");
        for (const l of c.logos)
            lines.push(`- \`${l.path}\`: ${l.name}${l.use ? `, ${l.use}` : ""}`);
        lines.push("");
    }
    if (c.tones?.length) {
        lines.push("## Tones", "", "| Tone | Colour | Use |", "|------|--------|-----|");
        for (const t of c.tones)
            lines.push(`| ${t.name} (\`${t.id}\`) | \`${t.colour}\` | ${t.use ?? ""} |`);
        lines.push("");
    }
    const others = Object.keys(entry.files ?? {}).filter((p) => !css.includes(p));
    if (others.length)
        lines.push("## References", "", ...others.map((p) => `- \`${p}\``), "");
    return {
        name: entry.id,
        description,
        skillMd: `${lines.join("\n").trimEnd()}\n`,
        files: [...Object.entries(entry.files ?? {}).map(([path, text]) => ({ path, text })), ...assets.map((a) => ({ path: a.path, asset: a.asset, url: assetUrl(a.asset) }))],
    };
}
// ---------------------------------------------------------------------------------------------
// Round 2 (AIOE 0.26.0): a design system as a zip, so it moves between organisations and to a
// team hub. Additive: nothing above changes.
//
//   POST /admin/v1/catalogue/design-systems/import         administrators: the zip (raw bytes, or a file in
//                                                          multipart/form-data), at most 40 MB -> 200
//                                                          DesignSystemImport (stored assets, entry not yet published)
//                                                          or 400 { error: "invalid_design_system", message, problems }
//   GET  /admin/v1/catalogue/design-systems/:id/export.zip administrators: the published system as a zip
//                                                          (?version= for an earlier one)
//
// The zip holds `design-system.json` (a {@link designSystemPackageSchema}: the content with paths
// where the entry has asset ids), `README.md` when the JSON has no `readme`, and every file the JSON
// names at its path: the tokens and art stylesheets and `files` as text, fonts, logos and asset
// stylesheets as bytes. The JSON may sit in a folder; that folder is the root.
// ---------------------------------------------------------------------------------------------
/** The description file at a design system zip's root. */
export const DESIGN_SYSTEM_PACKAGE_FILE = "design-system.json";
/** A design system's zip, at most. */
export const DESIGN_SYSTEM_PACKAGE_MAX_BYTES = 40 * 1024 * 1024;
/** `design-system.json`: the content, with each asset named by its path in the zip rather than its id. */
export const designSystemPackageSchema = designSystemContentSchema.extend({
    /** The catalogue id it was exported under: imported into an organisation that has it, it becomes a new version of that entry. */
    id: z.string().min(1).max(100).optional(),
    /** The entry's one-line summary. */
    summary: z.string().max(2000).optional(),
    /** Markdown the model reads first; absent, `README.md` beside the JSON is read. */
    readme: z.string().min(1).max(64 * 1024).optional(),
    /** Further text files kept in the entry's `files` (references, example components), by path. */
    files: z.array(catalogueFilePathSchema).max(100).optional(),
    stylesheets: z.array(designSystemStylesheetSchema.omit({ asset: true })).max(8).optional(),
    fonts: z.array(designSystemFontSchema.extend({ files: z.array(designSystemFontFileSchema.omit({ asset: true })).min(1).max(24) })).max(12).optional(),
    logos: z.array(designSystemLogoSchema.omit({ asset: true })).max(40).optional(),
    /** Absent reads as `offered`. */
    delivery: customisationDeliverySchema.optional(),
});
/** An asset's type from its path's extension, or undefined when it is not one a design system may hold. */
export function designSystemAssetTypeOf(path) {
    const ext = path.split(".").pop()?.toLowerCase() ?? "";
    const byExt = { woff2: "font/woff2", woff: "font/woff", ttf: "font/ttf", otf: "font/otf", svg: "image/svg+xml", png: "image/png", webp: "image/webp", jpg: "image/jpeg", jpeg: "image/jpeg", css: "text/css" };
    return byExt[ext];
}
/** What a package names: the text files (into the entry's `files`) and the assets, by path and kind. */
export function designSystemPackagePaths(p) {
    return {
        texts: [...new Set([p.tokens, ...(p.art ? [p.art] : []), ...(p.files ?? [])])],
        assets: [...(p.stylesheets ?? []).map((s) => ({ path: s.path, kind: "stylesheet" })), ...(p.fonts ?? []).flatMap((f) => f.files.map((x) => ({ path: x.path, kind: "font" }))), ...(p.logos ?? []).map((l) => ({ path: l.path, kind: "logo" }))],
    };
}
/**
 * A package and the files it names into a catalogue entry: `texts` by path, `assets` path to id
 * (sha256). The entry is not checked here: {@link designSystemProblems} and the platform's asset
 * checks do that.
 */
export function designSystemFromPackage(p, opts) {
    const { id: _id, summary, files: _files, readme, stylesheets, fonts, logos, delivery, ...rest } = p;
    const asset = (path) => opts.assets[path] ?? "";
    const content = {
        ...rest,
        readme: readme ?? opts.readme ?? "",
        ...(stylesheets?.length ? { stylesheets: stylesheets.map((s) => ({ ...s, asset: asset(s.path) })) } : {}),
        ...(fonts?.length ? { fonts: fonts.map((f) => ({ ...f, files: f.files.map((x) => ({ ...x, asset: asset(x.path) })) })) } : {}),
        ...(logos?.length ? { logos: logos.map((l) => ({ ...l, asset: asset(l.path) })) } : {}),
        delivery: delivery ?? "offered",
    };
    return { id: opts.id, kind: DESIGN_SYSTEM_KIND, name: p.name, ...(summary ? { summary } : {}), version: p.version, content, files: opts.texts };
}
/**
 * A published design system as a package: the JSON (without the organisation's own choices, the
 * default and the console's), its README, the text files and the assets by path and id.
 */
export function designSystemPackage(entry) {
    const c = designSystemContentSchema.parse(entry.content);
    const { readme, stylesheets, fonts, logos, useByDefault: _d, consoleUse: _c, ...rest } = c;
    const texts = { ...(entry.files ?? {}) };
    const others = Object.keys(texts).filter((p) => p !== c.tokens && p !== c.art);
    const manifest = {
        id: entry.id,
        ...(entry.summary ? { summary: entry.summary } : {}),
        ...rest,
        ...(others.length ? { files: others } : {}),
        ...(stylesheets?.length ? { stylesheets: stylesheets.map(({ asset: _a, ...s }) => s) } : {}),
        ...(fonts?.length ? { fonts: fonts.map((f) => ({ ...f, files: f.files.map(({ asset: _a, ...x }) => x) })) } : {}),
        ...(logos?.length ? { logos: logos.map(({ asset: _a, ...l }) => l) } : {}),
    };
    return { manifest, readme, texts, assets: designSystemAssets(c).map((a) => ({ path: a.path, asset: a.asset })) };
}
// ---------------------------------------------------------------------------------------------
// Round 3 (docs/plans/2026-10-design-systems.md, "Round 3 as built"): where each version came
// from, what changed in it, the fonts' licences reviewed on the way in, and the zip for team hubs.
// Additive: nothing above changes, and a reader that knows only the fields above reads every
// entry as before (the content schema drops what it does not know).
//
//   PUT  /admin/v1/catalogue/design-systems/:id            administrators: publish a design system with its
//                                                          round-3 fields (`content.changes`, `content.origin`),
//                                                          otherwise as PUT /admin/v1/catalogue/:id -> 204
//   POST /catalogue/v1/submissions/design-system           a member (device or sign-in, as POST /catalogue/v1/submissions):
//                                                          the zip (raw bytes or a multipart file, at most 40 MB), checked
//                                                          and its files stored on arrival -> 201 { state: "published", id }
//                                                          (administrators, or anyone where members publish), 202
//                                                          { state: "pending", submissionId } otherwise (its files held 30
//                                                          days), 403 not_allowed when submissions are off, 400
//                                                          invalid_design_system { message, problems } in words
//   GET  /catalogue/v1/design-systems/:id/export.zip       a device or a person of the organisation: the published
//                                                          system as the zip the admin export gives (a team hub
//                                                          imports it); ETag, 304 on If-None-Match; audited once
//                                                          per device (or person) per version
//
// The entry's `content` may carry {@link designSystemNotesSchema}: `changes` (what changed in this
// version, written by its publisher, shown in the version list and served to machines with the
// version) and `origin` (where this version came from: made in the console, a zip, or a template).
// `GET /admin/v1/catalogue/:id/versions` adds both to each version ({@link DesignSystemVersionNotes}).
// `design-system.json` may carry `origin` ({@link designSystemPackageOriginSchema}, written by the
// export) and `changes`. An import whose fonts say no licence is refused, in words, with
// `unlicensedFonts` beside `problems`.
// ---------------------------------------------------------------------------------------------
/** "What changed" in one version, at most. */
export const DESIGN_SYSTEM_CHANGES_MAX = 4000;
/** Where a design system's zip says it came from (`origin` in `design-system.json`, written by the export). */
export const designSystemPackageOriginSchema = z.object({
    /** The organisation that exported it, as people say it. */
    organisation: z.string().max(200).optional(),
    /** When it was exported (ISO 8601). */
    exportedAt: z.string().max(40).optional(),
    /** Where that organisation had it from, in words, when it was not made there ("the Mojo Up design language template"). */
    source: z.string().max(500).optional(),
});
/** Where one version of a design system came from. Absent on an entry from before round 3: say nothing. */
export const designSystemOriginSchema = z.object({
    /** `console`: written in the console; `zip`: imported from a zip or a folder; `template`: started from a template. */
    kind: z.enum(["console", "zip", "template"]),
    /** `zip`: the file's or folder's name as uploaded; `template`: the template's name. */
    source: z.string().max(200).optional(),
    /** `zip`: what its `design-system.json` says of where it came from. */
    from: designSystemPackageOriginSchema.optional(),
    /** Who brought it in (user id) and their name, and when: `zip` and `template`. */
    by: z.string().max(200).optional(),
    byName: z.string().max(200).optional(),
    at: z.string().max(40).optional(),
});
/** The round-3 fields a design system's `content` may carry beside {@link designSystemContentSchema}. */
export const designSystemNotesSchema = z.object({
    /** What changed in this version, in the publisher's words (plain text or short Markdown). */
    changes: z.string().trim().max(DESIGN_SYSTEM_CHANGES_MAX).optional(),
    origin: designSystemOriginSchema.optional(),
});
/** The round-3 fields a `design-system.json` may carry beside {@link designSystemPackageSchema}. */
export const designSystemPackageNotesSchema = z.object({
    origin: designSystemPackageOriginSchema.optional(),
    changes: z.string().trim().max(DESIGN_SYSTEM_CHANGES_MAX).optional(),
});
/** An entry's (or a package's) round-3 fields, read leniently: a field that does not parse is left out, an empty `changes` is none. */
export function designSystemNotes(content) {
    const c = (content && typeof content === "object" ? content : {});
    const changes = designSystemNotesSchema.shape.changes.safeParse(c.changes);
    const origin = designSystemOriginSchema.safeParse(c.origin);
    return { ...(changes.success && changes.data ? { changes: changes.data } : {}), ...(origin.success ? { origin: origin.data } : {}) };
}
/** Where a zip came from, in words: "Harbour Pty Ltd, exported 14 October 2026, from the Mojo Up design language template". */
export function designSystemPackageOriginWords(o) {
    if (!o)
        return undefined;
    const when = o.exportedAt && !Number.isNaN(Date.parse(o.exportedAt)) ? `exported ${dateLong(o.exportedAt, { timeZone: "UTC" })}` : undefined;
    const parts = [o.organisation, when, o.source ? `from ${o.source}` : undefined].filter(Boolean);
    return parts.length ? parts.join(", ") : undefined;
}
/**
 * Where a version came from, in words, for the console and an export's `origin.source`:
 * "Imported from harbour.zip (Harbour Pty Ltd, exported 14 October 2026)", "Started from the Mojo
 * Up design language template", "Written in the console". Undefined when the version does not say.
 */
export function designSystemOriginWords(o) {
    if (!o)
        return undefined;
    if (o.kind === "template")
        return o.source ? `Started from the ${o.source} template` : "Started from a template";
    if (o.kind === "console")
        return "Written in the console";
    const from = designSystemPackageOriginWords(o.from);
    return `Imported from ${o.source ?? "a zip"}${from ? ` (${from})` : ""}`;
}
/**
 * The fonts that say no licence, read from a `design-system.json` (or a content) before it is
 * parsed, so an import can list them by name rather than fail on the first. Round 1 decided a
 * font installed on every machine must say its licence: these are refused.
 */
export function unlicensedFonts(json) {
    const fonts = json && typeof json === "object" ? json.fonts : undefined;
    if (!Array.isArray(fonts))
        return [];
    const out = [];
    fonts.forEach((f, i) => {
        if (!f || typeof f !== "object")
            return;
        const font = f;
        if (typeof font.licence === "string" && font.licence.trim())
            return;
        const files = Array.isArray(font.files) ? font.files.map((x) => (x && typeof x === "object" && typeof x.path === "string" ? x.path : undefined)).filter((p) => !!p) : [];
        out.push({ name: typeof font.name === "string" && font.name.trim() ? font.name.trim() : `font ${i + 1}`, files });
    });
    return out;
}
/** The refusal's sentence for fonts without a licence statement. */
export function unlicensedFontsMessage(fonts) {
    const names = fonts.map((f) => f.name);
    const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
    return `${list} ${names.length === 1 ? "says" : "say"} no licence. A font installed on every machine must be licensed for it: add each font's licence (an SPDX id such as OFL-1.1, or in words) to design-system.json and upload it again.`;
}
// ---------------------------------------------------------------------------------------------
// Round 4 (docs/plans/2026-10-design-systems.md, "Round 4 as built"): an agent's design review
// against the system's checklist. Additive: nothing above changes.
//
//   POST /catalogue/v1/design-systems/:id/review      a device (desktop or node) or a person of the organisation:
//                                                     a DesignSystemReviewSubmit -> 201 DesignSystemReview;
//                                                     400 invalid_review { message, problems } in words (a rule id
//                                                     the version's checklist does not have, no subject); 404 no
//                                                     such system or version; 409 no_rules when the version has
//                                                     no component rules to review against. Audited.
//   GET  /catalogue/v1/design-systems/:id/reviews     the same callers: { reviews: DesignSystemReview[] }, newest
//                                                     first (?version= one version's, ?limit= at most 100, 20 by default)
//   GET  /catalogue/v1/design-systems/:id/checklist   the same callers: { version, rules: DesignSystemRule[] } for the
//                                                     served version (?version= an earlier one)
//
// `GET /admin/v1/catalogue/:id/versions` adds `review` ({@link DesignSystemReviewSummary}, the latest
// review of that version) to each version.
//
// **The checklist** is {@link designSystemChecklist}: the system's component rules (`content.rules`),
// its voice, and the list items of any reference file whose name says checklist or review, each a
// rule with an id (`rule-3`, `voice-1`, `check-12`) that a finding names. **DESIGN-REVIEW.md**
// ({@link DESIGN_REVIEW_FILE}): when a design system has rules, the platform writes the checklist,
// and how an agent reviews against it, into the entry's `files` on every publish
// ({@link withDesignReviewFile}), so a workbench agent receives it with the system and the skill
// lists it under References. A file of that name the publisher wrote themselves is left alone.
// ---------------------------------------------------------------------------------------------
import { CATALOGUE_FILES_MAX_BYTES } from "./catalogue.js";
/** The review instructions the platform writes into a design system's files when it has rules. */
export const DESIGN_REVIEW_FILE = "DESIGN-REVIEW.md";
/** The first line of a DESIGN-REVIEW.md the platform wrote (and may rewrite). */
export const DESIGN_REVIEW_GENERATED_MARK = "<!-- aioe:design-review: written by the platform from the design system's rules; publishing rewrites it -->";
/** Rules in one checklist, at most. */
export const DESIGN_REVIEW_RULES_MAX = 200;
/** Findings in one review, at most. */
export const DESIGN_REVIEW_FINDINGS_MAX = 200;
/** A reviewed page's HTML, at most (it is hashed and measured, not kept). */
export const DESIGN_REVIEW_HTML_MAX = 2 * 1024 * 1024;
/** Whether a DESIGN-REVIEW.md is the platform's own (rewritten on publish), not the publisher's. */
export const isGeneratedDesignReview = (text) => typeof text === "string" && text.startsWith(DESIGN_REVIEW_GENERATED_MARK);
const oneLine = (s, max) => {
    const t = s.replace(/\s+/g, " ").trim();
    return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
};
/** Markdown into rules: list items under headings, or headed sections, or (with neither) paragraphs. */
function markdownRules(md) {
    const lines = md.replace(/\r\n?/g, "\n").replace(/^(```|~~~)[\s\S]*?^\1[^\n]*$/gm, "").split("\n");
    const sections = [{ level: 0, body: [] }];
    for (const line of lines) {
        const h = /^(#{1,6})\s+(.+?)\s*#*\s*$/.exec(line);
        if (h)
            sections.push({ heading: h[2], level: h[1].length, body: [] });
        else
            sections[sections.length - 1].body.push(line);
    }
    const itemsOf = (body) => {
        const items = [];
        let open = false;
        for (const line of body) {
            const m = /^\s{0,3}(?:[-*+]|\d{1,3}[.)])\s+(?:\[[ xX]\]\s+)?(.*)$/.exec(line);
            if (m) {
                items.push(m[1]);
                open = true;
            }
            else if (open && /^\s{2,}\S/.test(line))
                items[items.length - 1] += ` ${line.trim()}`;
            else
                open = false;
        }
        return items.map((i) => i.trim()).filter(Boolean);
    };
    const paragraphs = (body) => body.join("\n").split(/\n\s*\n/).map((p) => p.trim()).filter((p) => p && !/^\s*(?:[-*+]|\d{1,3}[.)])\s/.test(p) && !/^\|/.test(p));
    const clean = (s) => s.replace(/^\d{1,3}[.)]\s+/, "").replace(/\*\*|__/g, "");
    const out = [];
    for (const s of sections) {
        const items = itemsOf(s.body);
        const section = s.heading && s.level > 1 ? clean(s.heading) : undefined;
        if (items.length)
            for (const i of items)
                out.push({ text: clean(i), ...(section ? { section } : {}) });
        else if (s.heading && s.level > 1) {
            const paras = paragraphs(s.body);
            const apply = paras.find((p) => /^\*\*(apply|how to apply|rule)\.?\*\*/i.test(p)) ?? paras[0];
            out.push({ text: clean(s.heading), ...(apply ? { detail: clean(apply.replace(/^\*\*(apply|how to apply|rule)\.?\*\*\s*/i, "")) } : {}) });
        }
    }
    if (out.length)
        return out;
    return paragraphs(lines).map((p) => ({ text: clean(p) }));
}
/**
 * A design system's review checklist: its component rules, its voice, and the list items of each
 * reference file whose name says checklist or review (not DESIGN-REVIEW.md), in that order, each
 * with an id a finding names. Empty when the content does not parse. At most
 * {@link DESIGN_REVIEW_RULES_MAX} rules.
 */
export function designSystemChecklist(entry) {
    const parsed = designSystemContentSchema.safeParse(entry.content);
    if (!parsed.success)
        return [];
    const c = parsed.data;
    const rules = [];
    const add = (prefix, from, md, source) => {
        if (!md?.trim())
            return;
        let n = rules.filter((r) => r.id.startsWith(`${prefix}-`)).length;
        for (const r of markdownRules(md)) {
            if (!r.text.trim())
                continue;
            rules.push({ id: `${prefix}-${++n}`, text: oneLine(r.text, 500), ...(r.detail ? { detail: oneLine(r.detail, 800) } : {}), ...(r.section ? { section: oneLine(r.section, 120) } : {}), from, ...(source ? { source } : {}) });
        }
    };
    add("rule", "rules", c.rules);
    add("voice", "voice", c.voice);
    const refs = Object.keys(entry.files ?? {})
        .filter((p) => p !== DESIGN_REVIEW_FILE && /\.md$/i.test(p) && /checklist|review/i.test(p.split("/").pop() ?? ""))
        .sort();
    for (const p of refs)
        add("check", "reference", entry.files[p], p);
    return rules.slice(0, DESIGN_REVIEW_RULES_MAX);
}
/**
 * DESIGN-REVIEW.md for a design system with rules: how an agent reviews a page, screen, deck or
 * document against the system, and every rule as a checklist line with its id. Undefined when the
 * system has no component rules.
 */
export function designReviewMarkdown(entry) {
    const parsed = designSystemContentSchema.safeParse(entry.content);
    if (!parsed.success || !parsed.data.rules?.trim())
        return undefined;
    const c = parsed.data;
    const rules = designSystemChecklist(entry);
    if (!rules.length)
        return undefined;
    const lines = [
        DESIGN_REVIEW_GENERATED_MARK,
        `# Design review: ${c.name} ${c.version}`,
        "",
        `Use this when asked to review a page, screen, deck, document or picture made with ${c.name}, and before you say such work is done.`,
        "",
        "## How to review",
        "",
        "1. Look at the work as a person would. For HTML, render it in light and dark and at a narrow and a wide width where you can; otherwise read the markup or the description you were given.",
        "2. Go through every rule below, in order. A rule the work breaks is a finding; a rule that does not apply to it is checked and passes.",
        "3. Write each finding in plain words: the rule's id, what is wrong, and where (a heading, a component, a selector). One finding per problem; no finding for taste the rules do not state.",
        `4. Send the review to the organisation's platform with \`POST /catalogue/v1/design-systems/${entry.id}/review\`: \`{ "version": "${c.version}", "title", "html" or "description", "checked": [rule ids], "findings": [{ "rule", "note", "where", "severity" }], "summary" }\`. Severity is \`must\` (breaks the system), \`should\` or \`note\`. The workbench sends it for you when it runs a design review.`,
        "",
        "## Checklist",
    ];
    const groups = [];
    for (const r of rules) {
        const title = r.from === "rules" ? "Component rules" : r.from === "voice" ? "Voice" : `From ${r.source}`;
        const g = groups.find(([t]) => t === title);
        if (g)
            g[1].push(r);
        else
            groups.push([title, [r]]);
    }
    for (const [title, list] of groups) {
        lines.push("", `### ${title}`, "");
        let section;
        for (const r of list) {
            if (r.section && r.section !== section)
                lines.push(`- ${r.section}:`);
            section = r.section;
            lines.push(`${r.section ? "  " : ""}- [ ] \`${r.id}\` ${r.detail ? `**${r.text.replace(/[.:;]$/, "")}.** ${r.detail}` : r.text}`);
        }
    }
    return `${lines.join("\n")}\n`;
}
/**
 * The entry with the platform's DESIGN-REVIEW.md in its `files`: written when the design system has
 * rules, rewritten when it changes, removed when the rules are gone; left out when the files would
 * pass {@link CATALOGUE_FILES_MAX_BYTES}. A DESIGN-REVIEW.md the publisher wrote is kept as it is.
 * Any other entry comes back unchanged.
 */
export function withDesignReviewFile(entry) {
    if (entry.kind !== DESIGN_SYSTEM_KIND)
        return entry;
    const own = entry.files?.[DESIGN_REVIEW_FILE];
    if (own !== undefined && !isGeneratedDesignReview(own))
        return entry;
    const { [DESIGN_REVIEW_FILE]: _old, ...rest } = entry.files ?? {};
    const text = designReviewMarkdown({ ...entry, files: rest });
    const size = (f) => Object.entries(f).reduce((n, [k, v]) => n + new TextEncoder().encode(k).length + new TextEncoder().encode(v).length, 0);
    const files = text !== undefined && size({ ...rest, [DESIGN_REVIEW_FILE]: text }) <= CATALOGUE_FILES_MAX_BYTES ? { ...rest, [DESIGN_REVIEW_FILE]: text } : rest;
    const { files: _f, ...without } = entry;
    return Object.keys(files).length || entry.files ? { ...without, files } : without;
}
/** One thing a review found: the rule it breaks, by id, and what is wrong, in words. */
export const designSystemFindingSchema = z.object({
    /** The rule's id in the version's checklist (`rule-3`). */
    rule: z.string().trim().min(1).max(40),
    /** What is wrong, in plain words. */
    note: z.string().trim().min(1).max(2000),
    /** Where: a heading, a component, a selector. */
    where: z.string().trim().max(300).optional(),
    /** `must`: breaks the system; `should`: a lapse; `note`: worth a look. Absent reads as `should`. */
    severity: z.enum(["must", "should", "note"]).optional(),
});
/** `POST /catalogue/v1/design-systems/:id/review`: what was reviewed and what the agent found. */
export const designSystemReviewSubmitSchema = z
    .object({
    /** The version reviewed against; absent, the version the catalogue serves. */
    version: z.string().min(1).max(40).optional(),
    /** What the work is called ("The quarterly report"), shown with the review. */
    title: z.string().trim().max(200).optional(),
    /** The page's HTML. Hashed and measured, never kept or shown. */
    html: z.string().min(1).max(DESIGN_REVIEW_HTML_MAX).optional(),
    /** A description of a screenshot or of the work, in words; kept with the review. */
    description: z.string().trim().min(1).max(16 * 1024).optional(),
    /** The rules checked, by id; absent, every rule in the checklist. */
    checked: z.array(z.string().trim().min(1).max(40)).max(DESIGN_REVIEW_RULES_MAX).optional(),
    findings: z.array(designSystemFindingSchema).max(DESIGN_REVIEW_FINDINGS_MAX),
    /** The agent's own one-paragraph verdict. */
    summary: z.string().trim().max(2000).optional(),
    /** Which agent or runtime reviewed it ("Claude Code, design reviewer"). */
    agent: z.string().trim().max(200).optional(),
})
    .refine((r) => Boolean(r.html) !== Boolean(r.description), { message: "send the page's html or a description of it, one of the two" });
export const designSystemReviewSummary = (r) => ({
    id: r.id,
    at: r.at,
    ...(r.byName ? { byName: r.byName } : {}),
    ...(r.subject.title ? { title: r.subject.title } : {}),
    checked: r.checked.length,
    findings: r.findings.length,
    must: r.findings.filter((f) => f.severity === "must").length,
});
/** "12 rules checked, 2 findings (1 that breaks the system)", "12 rules checked, nothing found". */
export function designReviewWords(s) {
    const rules = `${s.checked} ${s.checked === 1 ? "rule" : "rules"} checked`;
    if (!s.findings)
        return `${rules}, nothing found`;
    return `${rules}, ${s.findings} ${s.findings === 1 ? "finding" : "findings"}${s.must ? ` (${s.must} that ${s.must === 1 ? "breaks" : "break"} the system)` : ""}`;
}
//# sourceMappingURL=designSystems.js.map